import Immutable from 'immutable';
import moment from 'moment';
import React from 'react';

import Tab from 'sharedreact/components/Tab';
import Toolbar from 'sharedreact/components/Toolbar';
import ToolbarGroup from 'sharedreact/components/ToolbarGroup';
import ToolbarTitle from 'sharedreact/components/ToolbarTitle';
import TableContainer from 'sharedreact/containers/TableContainer';
import SortOrder from 'sharedreact/data/SortOrder';
import TableColumn from 'sharedreact/data/TableColumn';
import FileSupport from 'sharedreact/utils/FileSupport';
import { translate } from 'sharedreact/utils/LanguageSupport';

import type ViewSession from 'workspaces/launcher/data/ViewSession';

type Props = {
  index: number,
  activeTab: number,
  language: string,
  sortOrder: SortOrder,
  sessions: Immutable.List<ViewSession>,
  isFetching: boolean,

  onSortOrderChange: (sortOrder: SortOrder) => void,
  onLoad: () => void,
  onLeave: () => void
};

class ViewSessionsTab extends React.PureComponent<Props> {
  override componentDidMount (): void {
    if (this.props.index === this.props.activeTab) {
      setTimeout(() => this.props.onLoad(), 0);
    }
  }

  override componentDidUpdate (prevProps: Readonly<Props>): void {
    if ((this.props.activeTab !== prevProps.activeTab || this.props.index !== prevProps.index) &&
    this.props.index === this.props.activeTab) {
      setTimeout(() => this.props.onLoad(), 0);
    }
  }

  override componentWillUnmount (): void {
    setTimeout(() => this.props.onLeave(), 0);
  }

  renderToolbar () {
    const {
       language // eslint-disable-line no-unused-vars
    } = this.props;

    return (
      <Toolbar>
        <ToolbarGroup>
          <ToolbarTitle title={translate('launcher.admin.viewsessions.title')} />
        </ToolbarGroup>
      </Toolbar>);
  }

  override render (): React.ReactNode {
    const {
      index, activeTab, sessions, sortOrder, onSortOrderChange, isFetching
    } = this.props;
    if (index !== activeTab) return null;

    if (index !== activeTab || !sessions) {
      return null;
    }

    const tableColumns: Immutable.List<TableColumn<ViewSession>> = Immutable.List([{
      title: translate('launcher.admin.viewsessions.user'),
      width: '20%',
      renderCell: (item: ViewSession, row: number, col: number) => (
        item.user
      ),
      sortOrderKeyword: 'user',
      selectable: true,
      movable: true,
      name: 'user'
    }, {
      title: translate('launcher.admin.viewsessions.started'),
      width: '15%',
      renderCell: (item: ViewSession, row: number, col: number) => {
        return (
          (item.startedDate != null && moment.isMoment(item.startedDate))
            ? item.startedDate.locale(moment.locale()).format('LLL') : '-'
        );
      },
      sortOrderKeyword: 'started',
      selectable: true,
      movable: true,
      name: 'started'
    }, {
      title: translate('launcher.admin.viewsessions.documents'),
      width: '45%',
      renderCell: (item: ViewSession, row: number, col: number) => {
        return (
          <div className='webappslauncher-viewsessions-files'>
            {item.documents!.map((path, index) => {
              const extensionSepPos: number = path.lastIndexOf('.');
              const extension = extensionSepPos !== -1 ? path.substr(extensionSepPos + 1) : '';
              const lastSeparator: number = path.replace(/\\/g, '/').lastIndexOf('/');
              const filename: string = path.substring(lastSeparator + 1);

              return (
                <div className='webappslauncher-viewsessions-file' key={'line_' + index}>
                  <div className='webappslauncher-viewsessions-file-icon'>
                    <span className={FileSupport.getFileIconForType(extension)} />
                  </div>
                  <div className='webappslauncher-viewsessions-file-name'>
                    <span>
                      {filename}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>);
      },
      renderToolTip: (item: ViewSession, row: number, col: number) => {
        return (
          item.documents!.join('\n'))},

      sortOrderKeyword: 'documents',
      selectable: true,
      movable: true,
      name: 'documents'
    }, {
      title: translate('launcher.admin.viewsessions.host'),
      width: '20%',
      renderCell: (item: ViewSession, row: number, col: number) => (
        item.host
      ),
      sortOrderKeyword: 'host',
      selectable: true,
      movable: true,
      name: 'host'
    }, {
      title: translate('launcher.admin.viewsessions.viewengine'),
      width: '20%',
      renderCell: (item: ViewSession, row: number, col: number) => (
        translate('launcher.admin.viewsessions.viewengine.' + item.viewEngine.toLowerCase())
      ),
      sortOrderKeyword: 'viewengine',
      selectable: true,
      movable: true,
      name: 'viewengine'
    }, {
      title: translate('launcher.admin.viewsessions.remotehost'),
      width: '20%',
      renderCell: (item: ViewSession, row: number, col: number) => (
        item.remoteHost
      ),
      sortOrderKeyword: 'remotehost',
      selectable: true,
      movable: true,
      name: 'remotehost'
    }]);

    return (
      <Tab index={index} activeIndex={activeTab}>
        {this.renderToolbar()}
        <div className='webappslauncher-scroll-container'>
          <div className='webappslauncher-stretch-container'>
            <TableContainer<ViewSession>
              id='ae_viewsessions_table'
              tableName='ViewSessionsTable'
              tableColumns={tableColumns}
              rowData={sessions}
              sortOrder={sortOrder}
              isFetching={isFetching}
              onSortOrderChange={onSortOrderChange}
            />
          </div>
        </div>
      </Tab>
    );
  }
}

export default ViewSessionsTab;
